/**
 * Synchronisations-Service
 * Verwaltet Offline/Online Synchronisation mit Frappe Backend
 */

import { frappeClient } from './frappe';
import {
  productsStorage,
  invoicesStorage,
  zReportsStorage,
  syncErrorsStorage,
  syncStorage,
  settingsStorage
} from './storage';
import type { Product, SalesInvoice, ZReport, SyncError, SyncStatus } from '../types';

class SyncService {
  private isSyncing = false;
  private syncListeners: Array<(status: SyncStatus) => void> = [];

  /**
   * Registriert Sync-Status Listener
   */
  onStatusChange(listener: (status: SyncStatus) => void): () => void {
    this.syncListeners.push(listener);
    return () => {
      const index = this.syncListeners.indexOf(listener);
      if (index > -1) {
        this.syncListeners.splice(index, 1);
      }
    };
  }

  /**
   * Benachrichtigt alle Listener über Status-Änderung
   */
  notifyStatus(status: Partial<SyncStatus>): void {
    const fullStatus = this.getStatus();
    const newStatus = { ...fullStatus, ...status };
    this.syncListeners.forEach((listener) => listener(newStatus));
  }

  /**
   * Prüft ob Online-Verbindung verfügbar
   */
  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Gibt aktuellen Sync-Status zurück
   */
  getStatus(): SyncStatus {
    const pendingInvoices = invoicesStorage.getPending();
    const pendingReports = zReportsStorage.getPending();
    const errors = syncErrorsStorage.load();

    return {
      isOnline: this.isOnline(),
      isSyncing: this.isSyncing,
      lastSync: syncStorage.loadLastSync() || undefined,
      pendingItems: pendingInvoices.length + pendingReports.length,
      errors
    };
  }

  /**
   * Synchronisiert alle ausstehenden Daten
   */
  async syncAll(): Promise<void> {
    if (this.isSyncing) {
      console.log('Sync bereits in Progress');
      return;
    }

    if (!this.isOnline()) {
      console.log('Offline - Sync nicht möglich');
      return;
    }

    this.isSyncing = true;
    this.notifyStatus({ isSyncing: true });

    try {
      // Prüfe Server-Verbindung
      const isConnected = await frappeClient.ping();
      if (!isConnected) {
        throw new Error('Server nicht erreichbar');
      }

      // Synchronisiere Produkte
      await this.syncProducts();

      // Synchronisiere Rechnungen
      await this.syncInvoices();

      // Synchronisiere Z-Reports
      await this.syncZReports();

      // Speichere Sync-Timestamp
      syncStorage.saveLastSync(new Date().toISOString());

      this.notifyStatus({
        isSyncing: false,
        lastSync: new Date().toISOString(),
        pendingItems: 0
      });
    } catch (error) {
      console.error('Sync fehlgeschlagen:', error);
      this.notifyStatus({ isSyncing: false });
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Synchronisiert Produkte
   */
  private async syncProducts(): Promise<void> {
    try {
      const products = await frappeClient.getProducts();
      productsStorage.save(products);
      console.log(`✓ ${products.length} Produkte synchronisiert`);
    } catch (error) {
      console.error('Produkt-Sync fehlgeschlagen:', error);
      throw error;
    }
  }

  /**
   * Synchronisiert ausstehende Rechnungen
   */
  private async syncInvoices(): Promise<void> {
    const pendingInvoices = invoicesStorage.getPending();
    const settings = settingsStorage.load();

    for (const invoice of pendingInvoices) {
      try {
        const response = await frappeClient.syncInvoice(invoice, {
          revenue_account: settings.revenue_account,
          cash_account: settings.cash_account,
          sumup_account: settings.sumup_account,
          payment_fees_account: settings.payment_fees_account,
          sumup_fee_percentage: settings.sumup_fee_percentage,
          test_mode: settings.test_mode
        });
        // Speichere das 'name' Feld aus der Frappe Response als frappe_name
        invoicesStorage.update(invoice.id || '', {
          frappe_name: response.name,
          synced: true,
          synced_at: new Date().toISOString()
        });
        console.log(`✓ Rechnung ${invoice.id} synchronisiert (Frappe: ${response.name})`);
      } catch (error) {
        const syncError: SyncError = {
          id: `error_${Date.now()}_${invoice.id}`,
          type: 'invoice',
          item_id: invoice.id || '',
          error: error instanceof Error ? error.message : 'Unbekannter Fehler',
          timestamp: new Date().toISOString(),
          retry_count: 0
        };
        syncErrorsStorage.add(syncError);
        console.error(`✗ Rechnung ${invoice.id} Sync fehlgeschlagen:`, error);
      }
    }
  }

  /**
   * Synchronisiert ausstehende Z-Reports
   */
  private async syncZReports(): Promise<void> {
    const pendingReports = zReportsStorage.getPending();
    const settings = settingsStorage.load();

    for (const report of pendingReports) {
      try {
        // Erstelle Report ohne ID (Server erstellt die ID)
        const reportWithoutId: ZReport = {
          ...report,
          id: undefined
        };
        
        const response = await frappeClient.syncZReport(reportWithoutId, settings.test_mode || false);
        const frappeName = response.name; // Server-generierte ID
        const oldId = report.id;
        
        // Wenn Report eine temporäre ID hatte, entferne den alten und füge den neuen mit Server-ID hinzu
        if (oldId && oldId.startsWith('temp_zreport_')) {
          // Entferne alten Report mit temporärer ID
          const allReports = zReportsStorage.load();
          const filtered = allReports.filter((r) => r.id !== oldId);
          zReportsStorage.save(filtered);
          
          // Füge neuen Report mit Server-ID hinzu
          zReportsStorage.add({
            ...report,
            id: frappeName,
            synced: true,
            synced_at: new Date().toISOString()
          });
        } else if (oldId) {
          // Update bestehenden Report mit Server-ID
          zReportsStorage.update(oldId, {
            id: frappeName,
            synced: true,
            synced_at: new Date().toISOString()
          });
        } else {
          // Keine ID vorhanden, füge einfach hinzu
          zReportsStorage.add({
            ...report,
            id: frappeName,
            synced: true,
            synced_at: new Date().toISOString()
          });
        }
        
        console.log(`✓ Z-Report ${frappeName} synchronisiert`);
      } catch (error) {
        const syncError: SyncError = {
          id: `error_${Date.now()}_${report.id || 'unknown'}`,
          type: 'zreport',
          item_id: report.id || '',
          error: error instanceof Error ? error.message : 'Unbekannter Fehler',
          timestamp: new Date().toISOString(),
          retry_count: 0
        };
        syncErrorsStorage.add(syncError);
        console.error(`✗ Z-Report ${report.id || 'unknown'} Sync fehlgeschlagen:`, error);
      }
    }
  }

  /**
   * Retry für fehlgeschlagene Syncs
   */
  async retryFailedSyncs(): Promise<void> {
    const errors = syncErrorsStorage.load();
    const retryableErrors = errors.filter((e) => e.retry_count < 3);

    for (const error of retryableErrors) {
      try {
        if (error.type === 'invoice') {
          const invoice = invoicesStorage
            .load()
            .find((inv) => inv.id === error.item_id);
          if (invoice && !invoice.synced) {
            await this.syncInvoices();
          }
        } else if (error.type === 'zreport') {
          const report = zReportsStorage
            .load()
            .find((r) => r.id === error.item_id);
          if (report && !report.synced) {
            await this.syncZReports();
          }
        }
      } catch (err) {
        console.error(`Retry fehlgeschlagen für ${error.id}:`, err);
      }
    }
  }
}

// Singleton Instance
export const syncService = new SyncService();

// Online/Offline Event Listener - nur im Browser
if (typeof window !== 'undefined') {
  // Warte bis DOM ready ist
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.addEventListener('online', () => {
        syncService.syncAll().catch(console.error);
      });

      window.addEventListener('offline', () => {
        syncService.notifyStatus({ isOnline: false });
      });
    });
  } else {
    window.addEventListener('online', () => {
      syncService.syncAll().catch(console.error);
    });

    window.addEventListener('offline', () => {
      syncService.notifyStatus({ isOnline: false });
    });
  }
}

export default syncService;

